import React, { FC } from "react";
import { Stack, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

import { CarouselItemConfigType } from "./types/carouselItem";
import "./styles/carouselItem.scss";
import { OUR_SERVICES } from "../pageRoutes";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] as const }
  }
};

const HomeCarouselItem: FC<CarouselItemConfigType> = (props) => {
  const { headingDetail, headingTitle, exploreButtonLabel, bgVideoUrl } = props;

  const navigate = useNavigate();
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Stack
      component={motion.div}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      style={{
        position: "relative",
        justifyContent: "center",
        alignItems: "center",
        color: "white",
        fontWeight: "bold",
        textAlign: "center",
        width: "100vw",
        height: "auto",
        margin: 0,
        padding: "0 3em",
        overflow: "hidden",
        backdropFilter: "brightness(1)",
        backgroundImage:
          "https://kivunova.s3.us-east-1.amazonaws.com/home-bg.jpg"
      }}
    >
      <video
        autoPlay
        loop
        muted
        playsInline
        className="video-element"
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          minWidth: "100%",
          minHeight: "100%",
          width: "auto",
          height: "auto",
          transform: "translate(-50%, -50%)",
          objectFit: "cover",
          zIndex: -1,
          backdropFilter: "brightness(0.5)"
        }}
      >
        <source src={bgVideoUrl} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      {!matchMD && (
        <motion.h2
          variants={itemVariants}
          style={{
            margin: 0,
            fontSize: "clamp(1.75rem, 4vw, 2.75rem)",
            fontWeight: 700,
            textShadow: "0 2px 20px rgba(0,0,0,0.3)"
          }}
        >
          {headingTitle}
        </motion.h2>
      )}
      <motion.p
        variants={itemVariants}
        style={{
          fontFamily: "var(--font-mono)",
          fontWeight: 300,
          fontSize: "small",
          maxWidth: "42ch",
          lineHeight: 1.6
        }}
      >
        {headingDetail}
      </motion.p>
      <Stack sx={{ padding: "2em 0" }}>
        <motion.button
          variants={itemVariants}
          onClick={() => navigate(OUR_SERVICES)}
          className="openModalButton"
          whileHover={{
            scale: 1.03,
            boxShadow:
              "0 0 24px var(--color-primary-glow, rgba(0, 150, 160, 0.4))"
          }}
          whileTap={{ scale: 0.98 }}
          transition={{ type: "spring", stiffness: 400, damping: 17 }}
        >
          {exploreButtonLabel || "Learn more.."}
          <motion.span
            style={{ display: "inline-block", marginLeft: 6 }}
            initial={{ x: 0 }}
            whileHover={{ x: 4 }}
          >
            →
          </motion.span>
        </motion.button>
      </Stack>
    </Stack>
  );
};

export default HomeCarouselItem;
